<script setup lang="ts">
  import { useLocale } from '@tundr/i18n';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { computed, useId, useSlots } from 'vue';
  import FontIcon from '../../FontIcon/FontIcon.vue';
  import { FieldSize, FormField } from '../FormField';
  import {
    fieldClass,
    fieldSmallClass,
    inputClass,
    inputSmallClass,
    stepButtonClass,
  } from './InputNumber.css';
  import type { InputNumberEmits, InputNumberProps } from './InputNumber.types';

  const props = withDefaults(defineProps<InputNumberProps>(), {
    step: 1,
    disabled: false,
    required: false,
    size: FieldSize.MD,
  });

  const emit = defineEmits<InputNumberEmits>();

  const { t } = useLocale('ds-components');
  const slots = useSlots();

  const autoId = useId();
  const inputId = computed(() => props.id ?? autoId);

  const isSmall = computed<boolean>(() => props.size === FieldSize.SM);

  const clamp = (value: number): number => {
    let next = value;
    if (props.min !== undefined) {
      next = Math.max(props.min, next);
    }
    if (props.max !== undefined) {
      next = Math.min(props.max, next);
    }
    return next;
  };

  const fallbackValue = computed(() => props.min ?? 0);

  const decrementDisabled = computed(
    () =>
      props.disabled ||
      (props.modelValue !== null &&
        props.min !== undefined &&
        props.modelValue <= props.min),
  );

  const incrementDisabled = computed(
    () =>
      props.disabled ||
      (props.modelValue !== null &&
        props.max !== undefined &&
        props.modelValue >= props.max),
  );

  const stepBy = (direction: 1 | -1) => {
    if (props.disabled) {
      return;
    }
    const next =
      props.modelValue === null
        ? clamp(fallbackValue.value)
        : clamp(props.modelValue + direction * props.step);
    emit('update:modelValue', next);
  };

  const onInput = (event: Event) => {
    const raw = (event.target as HTMLInputElement).value;
    if (raw === '') {
      emit('update:modelValue', null);
      return;
    }
    const parsed = Number.parseFloat(raw);
    emit('update:modelValue', Number.isNaN(parsed) ? null : parsed);
  };

  const onBlur = (event: FocusEvent) => {
    emit('blur', event);
    if (props.modelValue === null) {
      return;
    }
    const clamped = clamp(props.modelValue);
    if (clamped !== props.modelValue) {
      emit('update:modelValue', clamped);
    }
  };

  const decrementLabel = computed(
    () => props.decrementLabel ?? t('input_number.decrement_aria_label'),
  );
  const incrementLabel = computed(
    () => props.incrementLabel ?? t('input_number.increment_aria_label'),
  );
</script>

<template>
  <FormField
    :id="inputId"
    :label="label"
    :helper-text="helperText"
    :error="error"
    :warning="warning"
    :size="size"
    :disabled-reason="disabled ? disabledReason : undefined"
  >
    <template v-if="slots.extraLabel" #extraLabel>
      <slot name="extraLabel" />
    </template>

    <template #default="{ describedBy, hasError }">
      <div
        :class="[fieldClass, isSmall && fieldSmallClass]"
        :data-invalid="hasError ? '' : undefined"
        :data-disabled="disabled ? '' : undefined"
      >
        <button
          type="button"
          :class="stepButtonClass"
          :aria-label="decrementLabel"
          :disabled="decrementDisabled"
          @click="stepBy(-1)"
        >
          <FontIcon :icon-name="TUNDR_ICONS.MINUS" />
        </button>

        <input
          :id="inputId"
          :class="[inputClass, isSmall && inputSmallClass]"
          type="number"
          inputmode="numeric"
          :name="name"
          :value="modelValue ?? ''"
          :min="min"
          :max="max"
          :step="step"
          :placeholder="placeholder"
          :disabled="disabled"
          :required="required"
          :aria-invalid="hasError ? 'true' : undefined"
          :aria-describedby="describedBy"
          @input="onInput"
          @blur="onBlur"
        >

        <button
          type="button"
          :class="stepButtonClass"
          :aria-label="incrementLabel"
          :disabled="incrementDisabled"
          @click="stepBy(1)"
        >
          <FontIcon :icon-name="TUNDR_ICONS.PLUS" />
        </button>
      </div>
    </template>
  </FormField>
</template>
